import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  Paper,
  Alert,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';

import { MethodEditorProps } from '../types';

interface ReduxGetOperation {
  reduxPath: string;
  contextKey: string;
}

interface ReduxSetOperation {
  value: any;
  reduxPath: string;
}

interface ReduxPayload {
  get?: ReduxGetOperation[];
  set?: ReduxSetOperation[];
}

export const ReduxEditor: React.FC<MethodEditorProps> = ({ payload, onChange }) => {
  const [newGetReduxPath, setNewGetReduxPath] = useState('');
  const [newGetContextKey, setNewGetContextKey] = useState('');
  const [newSetValue, setNewSetValue] = useState('');
  const [newSetReduxPath, setNewSetReduxPath] = useState('');

  const reduxPayload = (payload as ReduxPayload) || { get: [], set: [] };

  const addGetOperation = () => {
    if (newGetReduxPath && newGetContextKey) {
      onChange({
        ...reduxPayload,
        get: [
          ...(reduxPayload.get || []),
          { reduxPath: newGetReduxPath, contextKey: newGetContextKey },
        ],
      });
      setNewGetReduxPath('');
      setNewGetContextKey('');
    }
  };

  const removeGetOperation = (index: number) => {
    const updatedGet = [...(reduxPayload.get || [])];
    updatedGet.splice(index, 1);
    onChange({ ...reduxPayload, get: updatedGet });
  };

  const addSetOperation = () => {
    if (newSetValue && newSetReduxPath) {
      onChange({
        ...reduxPayload,
        set: [...(reduxPayload.set || []), { value: newSetValue, reduxPath: newSetReduxPath }],
      });
      setNewSetValue('');
      setNewSetReduxPath('');
    }
  };

  const removeSetOperation = (index: number) => {
    const updatedSet = [...(reduxPayload.set || [])];
    updatedSet.splice(index, 1);
    onChange({ ...reduxPayload, set: updatedSet });
  };

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Advanced Feature:</strong> This method directly accesses Redux state, bypassing
          normal boundaries. Use with caution as it can modify any part of the application state.
        </Typography>
      </Alert>

      {/* GET Operations Section */}
      <Box mb={3}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
          GET Operations
          <Typography variant="caption" display="block" color="text.secondary">
            Read from Redux state and store in context
          </Typography>
        </Typography>

        {/* Existing GET operations */}
        {reduxPayload.get?.map((operation, index) => (
          <Paper key={index} sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              value={operation.reduxPath}
              onChange={(e) => {
                const updatedGet = [...(reduxPayload.get || [])];
                updatedGet[index] = { ...operation, reduxPath: e.target.value };
                onChange({ ...reduxPayload, get: updatedGet });
              }}
              label="Redux Path"
              placeholder="entities.user.organization.brandColor"
              sx={{ flex: 1 }}
            />
            <ArrowIcon color="action" />
            <TextField
              size="small"
              value={operation.contextKey}
              onChange={(e) => {
                const updatedGet = [...(reduxPayload.get || [])];
                updatedGet[index] = { ...operation, contextKey: e.target.value };
                onChange({ ...reduxPayload, get: updatedGet });
              }}
              label="Context Key"
              placeholder="brandColor"
              sx={{ flex: 1 }}
            />
            <IconButton onClick={() => removeGetOperation(index)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Paper>
        ))}

        {/* Add new GET operation */}
        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.50' }}>
          <TextField
            size="small"
            value={newGetReduxPath}
            onChange={(e) => setNewGetReduxPath(e.target.value)}
            label="Redux Path"
            placeholder="entities.user.organization.name"
            sx={{ flex: 1 }}
          />
          <ArrowIcon color="action" />
          <TextField
            size="small"
            value={newGetContextKey}
            onChange={(e) => setNewGetContextKey(e.target.value)}
            label="Context Key"
            placeholder="orgName"
            sx={{ flex: 1 }}
          />
          <Button onClick={addGetOperation} startIcon={<AddIcon />} size="small">
            Add
          </Button>
        </Paper>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* SET Operations Section */}
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
          SET Operations (Work in Progress)
          <Typography variant="caption" display="block" color="text.secondary">
            Set Redux state with direct values or context templates
          </Typography>
        </Typography>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> SET operations are currently a work in progress and may not
            function correctly.
          </Typography>
        </Alert>

        {/* Existing SET operations */}
        {reduxPayload.set?.map((operation, index) => (
          <Paper key={index} sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              value={operation.value}
              onChange={(e) => {
                const updatedSet = [...(reduxPayload.set || [])];
                updatedSet[index] = { ...operation, value: e.target.value };
                onChange({ ...reduxPayload, set: updatedSet });
              }}
              label="Value"
              placeholder="#FF5733 or {{brandColor}}"
              sx={{ flex: 1 }}
            />
            <ArrowIcon color="action" />
            <TextField
              size="small"
              value={operation.reduxPath}
              onChange={(e) => {
                const updatedSet = [...(reduxPayload.set || [])];
                updatedSet[index] = { ...operation, reduxPath: e.target.value };
                onChange({ ...reduxPayload, set: updatedSet });
              }}
              label="Redux Path"
              placeholder="entities.user.organization.brandColor"
              sx={{ flex: 1 }}
            />
            <IconButton onClick={() => removeSetOperation(index)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Paper>
        ))}

        {/* Add new SET operation */}
        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.50' }}>
          <TextField
            size="small"
            value={newSetValue}
            onChange={(e) => setNewSetValue(e.target.value)}
            label="Value"
            placeholder="dark or {{theme}}"
            sx={{ flex: 1 }}
          />
          <ArrowIcon color="action" />
          <TextField
            size="small"
            value={newSetReduxPath}
            onChange={(e) => setNewSetReduxPath(e.target.value)}
            label="Redux Path"
            placeholder="entities.user.preferences.theme"
            sx={{ flex: 1 }}
          />
          <Button onClick={addSetOperation} startIcon={<AddIcon />} size="small">
            Add
          </Button>
        </Paper>
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Path Format:</strong> Use dot notation for paths (e.g.,
          &ldquo;entities.user.organization.id&rdquo;)
          <br />
          <strong>JSONPath:</strong> Prefix with $ for advanced queries (e.g.,
          &ldquo;$.entities.campaigns[0].title&rdquo;)
          <br />
          <strong>Templates:</strong> Use {'{{contextKey}}'} to reference context values in SET
          operations
        </Typography>
      </Alert>
    </Box>
  );
};
